import { Alert, Badge } from '@/components/ui';
import { cn } from '@/utils/cn';

import { MatchConfidence } from './MatchBadge';

/**
 * Why the score is what it is.
 *
 * A dimension with a `null` score was not assessed, and that is rendered as
 * "not compared" rather than as an empty bar -- an empty bar and a zero look
 * the same and mean opposite things.
 *
 * `hide` drops dimensions by key from the list only -- the overall score still
 * counts them. `compact` lays the rows out two to a line with tighter spacing.
 */
export function MatchBreakdown({ match, advice = true, hide = [], compact = false, className }) {
  if (!match) return null;

  const shown = match.dimensions.filter((entry) => !hide.includes(entry.key));
  const scored = shown.filter((entry) => entry.score !== null);
  const unscored = shown.filter((entry) => entry.score === null);

  return (
    <div className={className}>
      <MatchConfidence match={match} className={cn('block', compact ? 'mb-2' : 'mb-3')} />

      {scored.length === 0 ? (
        <p className="text-ink-muted text-sm">
          There was nothing on both sides to compare, so no score is claimed.
        </p>
      ) : (
        <ul className={compact ? 'grid gap-x-6 gap-y-2.5 sm:grid-cols-2' : 'space-y-3'}>
          {scored.map((entry) => (
            <li key={entry.key} className="min-w-0">
              <div
                className={cn(
                  'flex items-baseline justify-between gap-3',
                  compact ? 'mb-0.5 text-xs' : 'mb-1 text-sm',
                )}
              >
                <span className="text-ink font-medium">{entry.label}</span>
                <span className="text-ink-muted text-xs">{entry.score}%</span>
              </div>
              <div
                className={cn(
                  'bg-surface-muted w-full overflow-hidden rounded-full',
                  compact ? 'h-1' : 'h-1.5',
                )}
              >
                <div
                  className={cn(
                    'h-full rounded-full',
                    entry.score >= 75
                      ? 'bg-emerald-500'
                      : entry.score >= 40
                        ? 'bg-brand-500'
                        : 'bg-amber-500',
                  )}
                  style={{ width: `${entry.score}%` }}
                />
              </div>
              <p
                className={cn('text-ink-muted text-xs', compact ? 'mt-0.5 truncate' : 'mt-1')}
                title={compact ? entry.reason : undefined}
              >
                {entry.reason}
              </p>
            </li>
          ))}
        </ul>
      )}

      {unscored.length > 0 && (
        <div className="mt-4">
          <p className="text-ink-muted mb-1.5 text-xs">Not compared</p>
          {/* Badge takes no tooltip, and the reason is the useful half
              anyway, so it is rendered rather than hidden behind a hover. */}
          <ul className="space-y-1">
            {unscored.map((entry) => (
              <li key={entry.key} className="text-ink-muted text-xs">
                <span className="text-ink">{entry.label}</span> — {entry.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {advice && match.missing_from_profile?.length > 0 && (
        <Alert tone="info" className="mt-4" title="Improve the estimate">
          <ul className="list-inside list-disc">
            {match.missing_from_profile.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Alert>
      )}
    </div>
  );
}

/** The one-line version: what helped and what did not. */
export function MatchHighlights({ match, className }) {
  if (!match || (match.strengths.length === 0 && match.gaps.length === 0)) return null;
  return (
    <ul className={cn('flex flex-wrap gap-1.5', className)}>
      {match.strengths.map((item) => (
        <li key={item}>
          <Badge tone="success">{item}</Badge>
        </li>
      ))}
      {match.gaps.map((item) => (
        <li key={item}>
          <Badge tone="warning">{item}</Badge>
        </li>
      ))}
    </ul>
  );
}
